// docs/71 — the hero inventory: the ten worn slots, which items fit where, what a plain Equip
// picks, and what putting an item on would move (the module's `items::wear`, mirrored so the paper
// doll can light the slots a dragged item fits and say what comes off before anything is sent).
// The server stays the judge; these only predict it. Pure functions; covered by gear.test.ts.
import type { ItemCard } from './items';
import { catalogueItem } from '../items/catalogue';
import { rarityWord } from '../items/describe';

/** `fk_grammar::GearSlot` by number (`item.equipped_slot`). */
export const SLOT = { MainHand: 0, OffHand: 1, Torso: 2, Head: 3, Cape: 4, Trinket: 5, Hands: 6, Feet: 7, Neck: 8, Trinket2: 9, Legs: 10, Waist: 11 } as const;
export type SlotName = keyof typeof SLOT;
/** docs/72 IT2: Legs and Waist appended after Trinket2, so every stored slot keeps its number. */
export const SLOT_COUNT = 12;
const SLOT_BY_NAME = SLOT as Record<string, number>;

export interface SlotInfo { slot: number; key: SlotName; label: string; hint: string; icon: string }
/** The paper doll's slots, with the greyed sprite an empty one shows. */
export const SLOTS: readonly SlotInfo[] = [
  { slot: 0, key: 'MainHand', label: 'Main hand', hint: 'A weapon. Two-handed ones need the off hand free.', icon: 'item_199' },
  { slot: 1, key: 'OffHand', label: 'Off hand', hint: 'A shield, a focus or a dagger beside a one-handed weapon.', icon: 'item_221' },
  { slot: 2, key: 'Torso', label: 'Body', hint: 'Armour. Never empty: swap it for another.', icon: 'item_266' },
  { slot: 3, key: 'Head', label: 'Head', hint: 'One helm or cap.', icon: 'item_276' },
  { slot: 4, key: 'Cape', label: 'Back', hint: 'One cape or cloak.', icon: 'item_250' },
  { slot: 5, key: 'Trinket', label: 'Ring', hint: 'A ring or a charm.', icon: 'item_076' },
  { slot: 6, key: 'Hands', label: 'Hands', hint: 'Gloves or gauntlets.', icon: 'item_259' },
  { slot: 7, key: 'Feet', label: 'Feet', hint: 'One pair of boots.', icon: 'item_292' },
  { slot: 8, key: 'Neck', label: 'Neck', hint: 'An amulet, pendant or torc.', icon: 'item_024' },
  { slot: 9, key: 'Trinket2', label: 'Ring', hint: 'A second ring or charm.', icon: 'item_076' },
  { slot: 10, key: 'Legs', label: 'Legs', hint: 'Greaves, leggings, trousers or a kilt.', icon: 'item_298' },
  { slot: 11, key: 'Waist', label: 'Waist', hint: 'A belt, girdle, sash or bandolier.', icon: 'item_235' },
];
export const slotLabel = (slot: number | null | undefined): string => (slot === null || slot === undefined ? 'Pack' : SLOTS[slot]?.label ?? `slot ${slot}`);

/** Tier names and tones: plain, fine, masterwork, and the hoard's legendary fourth. */
export const TIER_WORDS = ['', 'Plain', 'Fine', 'Masterwork', 'Legendary'] as const;
export const tierWord = (t: number): string => TIER_WORDS[t] ?? `Tier ${t}`;
/** The grade a piece is coloured by: a catalogue piece's rarity (1–4), else its tier. */
export const gradeTier = (def: number, tier: number): number => catalogueItem(def)?.rarity ?? tier;
/** The word for a piece's grade: a catalogue piece (docs/72) has a rarity (Common … Legendary), a
 * hand-authored one its tier (Plain … Legendary). */
export const gradeWord = (def: number, tier: number, relic = false): string => {
  if (relic) return 'Relic';
  const c = catalogueItem(def);
  return c ? rarityWord(c.rarity) : tierWord(tier);
};
export type Rarity = 'plain' | 'fine' | 'masterwork' | 'legendary' | 'relic';
export const rarityOf = (tier: number, relic = false): Rarity => (relic ? 'relic' : tier >= 4 ? 'legendary' : tier === 3 ? 'masterwork' : tier === 2 ? 'fine' : 'plain');

/** The pack a hero starts with and the ones a saddler sells (fk-world `PACK_SIZES`/`PACK_PRICES`). */
export const PACKS = [
  { capacity: 20, price: 0, name: "Traveller's pack" },
  { capacity: 25, price: 40, name: 'Stout rucksack' },
  { capacity: 30, price: 100, name: 'Pack-frame' },
  { capacity: 40, price: 220, name: "Mule's panniers" },
] as const;
export const PACK_SIZE = PACKS[0].capacity;

/** What a village pays for a piece before haggling (`fk_grammar::sell_price`): a quarter of its price,
 * all of it for treasure; a catalogue piece's price is its own or its rarity's (40 / 120 / 360 / 900). */
export function sellValue(def: number, tier: number): number {
  const c = catalogueItem(def);
  if (!c) return [0, 5, 15, 45][tier] ?? 0;
  const price = c.price || [0, 40, 120, 360, 900][c.rarity] || 0;
  return c.family === 'treasure' ? price : Math.floor(price / 4);
}

/** An item as the rules read it: the slots it fits (numbers, own slot first) and whether it takes both hands. */
export interface GearDef { slots: number[]; twoHanded: boolean; classMask: number; consumable: boolean }
const CLASS_BITS: Record<string, number> = { fighter: 1, rogue: 2, wizard: 4, cleric: 8, ranger: 16, paladin: 32, druid: 64, bard: 128 };
/** docs/104 NC1: `ClassMask` is a full u8 — eight classes. */
export const ANY_CLASS = 0xff;
/** The rules' reading of a catalogue piece without the wasm card (`fk_grammar::slots_for`): a ring or
 * charm fits either ring slot, a dagger either hand, everything else its own slot. */
export function catalogueGearDef(def: number): GearDef | undefined {
  const c = catalogueItem(def);
  if (!c) return undefined;
  const own = c.slot ? SLOT_BY_NAME[c.slot] : undefined;
  const slots = own === undefined ? [] : own === SLOT.Trinket ? [SLOT.Trinket, SLOT.Trinket2] : c.family === 'dagger' ? [SLOT.MainHand, SLOT.OffHand] : [own];
  const classMask = c.classes?.length ? c.classes.reduce((m, k) => m | (CLASS_BITS[k] ?? 0), 0) : ANY_CLASS;
  return { slots, twoHanded: !!c.two_handed, classMask, consumable: !!(c.use && 'fight' in c.use) };
}

export function gearDef(card: Pick<ItemCard, 'slot' | 'slots' | 'two_handed' | 'class_mask' | 'consumable'>): GearDef {
  const named = card.slots?.length ? card.slots : card.slot ? [card.slot] : [];
  return { slots: named.map((s) => SLOT_BY_NAME[s as string]).filter((n): n is number => n !== undefined), twoHanded: !!card.two_handed, classMask: card.class_mask ?? ANY_CLASS, consumable: card.consumable != null };
}

/** One carried item as the rules need it. */
export interface Carried { id: bigint; def: GearDef; slot: number | null; name: string; relic?: boolean }

/** What a plain Equip picks (`items::auto_slot`): the first empty slot it fits; a dagger beside a one-handed weapon goes to the off hand, never over the main hand. */
export function autoSlot(def: GearDef, worn: readonly Carried[]): number | undefined {
  const at = (s: number) => worn.find((w) => w.slot === s);
  if (def.slots.includes(SLOT.MainHand) && def.slots.includes(SLOT.OffHand)) {
    const main = at(SLOT.MainHand);
    if (!main) return SLOT.MainHand;
    return main.def.twoHanded ? undefined : SLOT.OffHand;
  }
  return def.slots.find((s) => !at(s)) ?? def.slots[0];
}

/** What wearing an item in a slot would do (`items::wear`): refused with a reason, or the pieces it moves. */
export type WearPlan =
  | { ok: false; reason: string }
  | { ok: true; noop?: boolean; toPack: Carried[]; swapped?: { item: Carried; to: number } ; packAfter: number };

/** `classBit` per `fk_grammar::class_bit` (class number = bit). */
export const classAllowed = (mask: number, cls: number): boolean => (mask & (1 << cls)) !== 0;

export function planWear(it: Carried, slot: number, carried: readonly Carried[], opts: { cls: number; capacity: number }): WearPlan {
  const name = it.name;
  if (!it.def.slots.length) return { ok: false, reason: it.def.consumable ? `${name} is used in a fight, not worn` : `${name} is not worn` };
  if (!it.def.slots.includes(slot)) return { ok: false, reason: `${name} does not go in the ${(SLOTS[slot]?.label ?? 'that').toLowerCase()} slot` };
  if (!classAllowed(it.def.classMask, opts.cls)) return { ok: false, reason: `this hero's calling cannot use ${name}` };
  const packNow = carried.filter((c) => c.slot === null).length;
  if (it.slot === slot) return { ok: true, noop: true, toPack: [], packAfter: packNow };
  const others = carried.filter((c) => c.id !== it.id);
  const at = (s: number) => others.find((o) => o.slot === s);
  const toPack: Carried[] = [];
  let swapped: { item: Carried; to: number } | undefined;
  const o = at(slot);
  if (o) {
    const from = it.slot;
    const back = from !== null && o.def.slots.includes(from) && !((from === SLOT.MainHand || from === SLOT.OffHand) && it.def.twoHanded) ? from : null;
    if (back === null && o.slot === SLOT.Torso && from !== null) return { ok: false, reason: 'the body is never bare: swap armour from the pack' };
    if (back !== null) swapped = { item: o, to: back };
    else toPack.push(o);
  }
  if (slot === SLOT.MainHand && it.def.twoHanded) { const off = at(SLOT.OffHand); if (off) toPack.push(off); }
  if (slot === SLOT.OffHand) { const main = at(SLOT.MainHand); if (main?.def.twoHanded) toPack.push(main); }
  const packAfter = packNow + toPack.length - (it.slot === null ? 1 : 0);
  if (packAfter > opts.capacity) return { ok: false, reason: `no room in the pack for the ${toPack.map((t) => t.name).join(' and ')}` };
  return { ok: true, toPack, swapped, packAfter };
}

/** Whether taking a worn item off fits (`items::unequip`). */
export function planUnwear(it: Carried, carried: readonly Carried[], capacity: number): { ok: true } | { ok: false; reason: string } {
  if (it.slot === null) return { ok: true };
  if (it.slot === SLOT.Torso) return { ok: false, reason: 'the body is never bare: swap armour instead' };
  if (carried.filter((c) => c.slot === null).length + 1 > capacity) return { ok: false, reason: 'the pack is full' };
  return { ok: true };
}

/** The worn set after a plan, as `[slot, def, tier]` triples for `preview_hero_gear`. */
export function wornAfter<T extends Carried & { defId: number; tier: number }>(carried: readonly T[], it: T, slot: number, plan: Extract<WearPlan, { ok: true }>): number[] {
  const out: number[] = [];
  const gone = new Set(plan.toPack.map((p) => p.id));
  for (const c of carried) {
    if (c.id === it.id) continue;
    if (gone.has(c.id)) continue;
    const s = plan.swapped?.item.id === c.id ? plan.swapped.to : c.slot;
    if (s !== null) out.push(s, c.defId, c.tier);
  }
  out.push(slot, it.defId, it.tier);
  return out;
}

/** docs/112: each piece's upgrades as `preview_hero_gear` reads them, in the order of its triples: `[n, id × n, …]`. */
export function upgradeSegs(list: readonly { upgrades?: readonly number[] }[]): number[] {
  return list.flatMap((i) => [i.upgrades?.length ?? 0, ...(i.upgrades ?? [])]);
}

/** `wornAfter`'s pieces' upgrades, in its order. */
export function wornAfterUps<T extends Carried & { upgrades?: readonly number[] }>(carried: readonly T[], it: T, plan: Extract<WearPlan, { ok: true }>): number[] {
  const gone = new Set(plan.toPack.map((p) => p.id));
  const order: T[] = [];
  for (const c of carried) {
    if (c.id === it.id || gone.has(c.id)) continue;
    const s = plan.swapped?.item.id === c.id ? plan.swapped.to : c.slot;
    if (s !== null) order.push(c);
  }
  order.push(it);
  return upgradeSegs(order);
}

/** The numbers the inventory shows, in the sheet's order, and how to read a change in each. */
export const STAT_ROWS = [
  { key: 'hp_max', label: 'HP', icon: 'item_085', better: 1 },
  { key: 'guard', label: 'Guard', icon: 'item_223', better: 1 },
  { key: 'weapon_damage', label: 'Weapon', icon: 'item_199', better: 1 },
  { key: 'power', label: 'Power', icon: 'item_108', better: 1 },
  { key: 'focus', label: 'Focus', icon: 'item_089', better: 1 },
  { key: 'move_tiles', label: 'Move', icon: 'item_287', better: 1 },
  { key: 'range', label: 'Range', icon: 'item_191', better: 1 },
  { key: 'resource', label: 'Resource', icon: 'item_007', better: 1 },
] as const;
export type StatKey = (typeof STAT_ROWS)[number]['key'];
export interface StatLike { hp_max: number; guard: number; weapon_damage: number; power: number; focus: number; move_tiles: number; range: number; resource: { max: number } }
export const statValue = (s: StatLike, k: StatKey): number => (k === 'resource' ? s.resource.max : s[k]);
/** Per-stat differences `next − now`, only the ones that change. */
export function statDiff(now: StatLike, next: StatLike): Partial<Record<StatKey, number>> {
  const out: Partial<Record<StatKey, number>> = {};
  for (const r of STAT_ROWS) { const d = statValue(next, r.key) - statValue(now, r.key); if (d) out[r.key] = d; }
  return out;
}

export type PackSort = 'newest' | 'kind' | 'tier' | 'name';
export const PACK_SORTS: readonly PackSort[] = ['newest', 'kind', 'tier', 'name'];
export const isPackSort = (v: unknown): v is PackSort => (PACK_SORTS as readonly unknown[]).includes(v);
export type PackFilter = 'all' | 'weapons' | 'armour' | 'trinkets' | 'use';
/** The filter group of an item by the slots it fits (a consumable has none). */
export function filterGroup(def: GearDef): Exclude<PackFilter, 'all'> {
  if (def.consumable || !def.slots.length) return 'use';
  const s = def.slots[0]!;
  if (s === SLOT.MainHand || s === SLOT.OffHand) return 'weapons';
  if (s === SLOT.Trinket || s === SLOT.Trinket2 || s === SLOT.Neck || s === SLOT.Waist) return 'trinkets';
  return 'armour';
}
const KIND_ORDER = (def: GearDef): number => (def.slots.length ? def.slots[0]! : 99);
export function sortPack<T extends { id: bigint; name: string; tier: number; def: GearDef }>(items: readonly T[], by: PackSort): T[] {
  const v = [...items];
  const newest = (a: T, b: T) => (a.id < b.id ? 1 : a.id > b.id ? -1 : 0);
  if (by === 'newest') return v.sort(newest);
  if (by === 'name') return v.sort((a, b) => a.name.localeCompare(b.name) || newest(a, b));
  if (by === 'tier') return v.sort((a, b) => b.tier - a.tier || KIND_ORDER(a.def) - KIND_ORDER(b.def) || newest(a, b));
  return v.sort((a, b) => KIND_ORDER(a.def) - KIND_ORDER(b.def) || b.tier - a.tier || newest(a, b));
}
